/**
 * @module VerifyErrorHandler
 * @description Phase 14 extraction of shared verify error-result shaping.
 * Keeps strategy failures normalized before ServiceTestVerifyHelper returns them to the detail page.
 */

import { t } from '@lingui/macro';

/**
 * Build an error result object.
 * Mirrors VoipVerifyHelper.buildVerifyErrorResult pattern.
 *
 * @param {Error|Object} error
 * @returns {{ status: string, statusMsg: string, resultType: string, resultData: Object|null }}
 */
function buildVerifyErrorResult(error) {
    const message = error?.message || error?.data?.message || '';

    // Handle specific VoIP error codes (from verify.service.js handleVerifyError)
    if (message === 'vtn_agents_already_in_call' || error?.responseBody?.message === 'vtn_agents_already_in_call') {
        return {
            status: t`Error`,
            statusMsg: t`One or more nPoints are already in a call. Please wait for the current call to finish and try again.`,
            resultType: null,
            resultData: null,
        };
    }
    if (
        message === 'voip_wireless_adapter_not_enabled' ||
        error?.responseBody?.message === 'voip_wireless_adapter_not_enabled'
    ) {
        return {
            status: t`Error`,
            statusMsg: t`The wireless adapter is not available. Please check the nPoint wireless configuration.`,
            resultType: null,
            resultData: null,
        };
    }

    // Handle validation errors (from verify.service.js handleVerifyError)
    const validationErrors = error?.data?.errors || error?.errors;
    if (validationErrors && Array.isArray(validationErrors)) {
        const messages = validationErrors.map((e) => e.message).filter(Boolean);
        return {
            status: t`Error`,
            statusMsg: messages.join('; ') || t`Validation error`,
            resultType: null,
            resultData: null,
        };
    }

    if (error?.response?.status === 400 && message === 'Bad Request') {
        return {
            status: t`Failure`,
            statusMsg: 'Bad Request, please try first Save to check what bad request error.',
            resultType: null,
            resultData: null,
        };
    }

    return {
        status: t`Failure`,
        statusMsg:
            message ||
            t`There was an error trying to verify this test. If this problem persists, please contact support.`,
        resultType: null,
        resultData: null,
    };
}

export { buildVerifyErrorResult };
